<template>
  <div class="container">
    <div class="d-flex flex-wrap align-center justify-space-between mb-4">
      <div class="mr-4">
        <div class="text-h5">API Settings</div>
        <div class="text-body-2 grey--text text--darken-1">
          Create API keys and control which browser origins may use them.
        </div>
      </div>
      <div class="d-flex flex-wrap mt-2 mt-sm-0">
        <v-btn class="mr-2 mb-2 mb-sm-0" text @click="loadApiKeys" :loading="loading">
          Refresh
        </v-btn>
        <v-btn class="mb-2 mb-sm-0" color="primary" @click="openCreateDialog">
          New API Key
        </v-btn>
      </div>
    </div>

    <v-alert dense outlined type="info" class="mb-4">
      Browser requests using <code>X-API-Key</code> are checked against the
      allowed origins on that key. Leave origins empty for server-to-server
      usage only.
    </v-alert>

    <v-progress-linear color="primary" indeterminate :active="loading" class="mb-4" />

    <v-card>
      <v-data-table
        :headers="headers"
        :items="apiKeys"
        :loading="loading"
        sort-by="createdAt"
        :sort-desc="true"
        class="api-key-table"
      >
        <template v-slot:item.isActive="{ item }">
          <v-chip x-small :color="item.isActive ? 'green lighten-4' : 'grey lighten-3'">
            {{ item.isActive ? "Active" : "Inactive" }}
          </v-chip>
        </template>

        <template v-slot:item.allowedOrigins="{ item }">
          <div v-if="item.allowedOrigins && item.allowedOrigins.length" class="py-2">
            <v-chip
              v-for="origin in item.allowedOrigins"
              :key="`${item.id}-${origin}`"
              x-small
              class="mr-1 mb-1"
            >
              {{ origin }}
            </v-chip>
          </div>
          <span v-else class="text-caption grey--text text--darken-1">
            Server-to-server only
          </span>
        </template>

        <template v-slot:item.lastUsedAt="{ item }">
          <span v-if="item.lastUsedAt">{{ item.lastUsedAt | formatDateTimeYear }}</span>
          <span v-else class="text-caption grey--text text--darken-1">Never</span>
        </template>

        <template v-slot:item.expiresAt="{ item }">
          <span v-if="item.expiresAt">{{ item.expiresAt | formatDateTimeYear }}</span>
          <span v-else class="text-caption grey--text text--darken-1">Never</span>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ item.createdAt | formatDateTimeYear }}
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="text-no-wrap">
            <v-btn icon small title="Edit API key" @click="openEditDialog(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              title="Deactivate API key"
              :disabled="!item.isActive"
              @click="deactivateKey(item)"
            >
              <v-icon small>mdi-cancel</v-icon>
            </v-btn>
            <v-btn icon small title="Delete API key" @click="deleteKey(item)">
              <v-icon small color="red darken-1">mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="editorDialog" max-width="720">
      <v-card>
        <v-card-title>{{ isEditing ? "Edit API Key" : "Create API Key" }}</v-card-title>
        <v-card-text>
          <v-form ref="editorForm" v-model="formValid" lazy-validation>
            <v-text-field
              v-model="form.keyName"
              label="Key Name"
              :rules="keyNameRules"
              counter="100"
              required
            />
            <v-textarea
              v-model="form.description"
              label="Description"
              rows="2"
              auto-grow
              counter="500"
            />
            <v-text-field
              v-model="form.expiresAt"
              label="Expires At"
              type="datetime-local"
              hint="Optional"
              persistent-hint
            />
            <v-combobox
              v-model="form.allowedOrigins"
              label="Allowed Browser Origins (CORS)"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              hide-selected
              persistent-hint
              hint="Enter full origins (e.g. https://factory.bergsoemetals.com, http://localhost:9083). Leave empty for server-to-server only (no browser restriction)."
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeEditor">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveApiKey">
            {{ isEditing ? "Save Changes" : "Create Key" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createdKeyDialog" max-width="760">
      <v-card>
        <v-card-title>Copy API Key Now</v-card-title>
        <v-card-text>
          <div class="mb-3">
            This is the only time the full API key will be shown.
          </div>
          <v-text-field
            :value="createdKey.apiKey"
            label="API Key"
            readonly
            append-icon="mdi-content-copy"
            @click:append="copyCreatedKey"
          />
          <div class="text-caption grey--text text--darken-1">
            Key: {{ createdKey.keyName || "n/a" }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="copyCreatedKey">Copy</v-btn>
          <v-btn color="primary" @click="closeCreatedKeyDialog">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import {
  createApiKey,
  deactivateApiKey,
  deleteApiKey,
  getAllApiKeys,
  updateApiKey,
} from "@/services/apiKeys";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      formValid: false,
      editorDialog: false,
      createdKeyDialog: false,
      isEditing: false,
      apiKeys: [],
      createdKey: {},
      headers: [
        { text: "Name", value: "keyName" },
        { text: "Status", value: "isActive" },
        { text: "Created By", value: "createdBy" },
        { text: "Allowed Origins", value: "allowedOrigins", sortable: false },
        { text: "Last Used", value: "lastUsedAt" },
        { text: "Expires", value: "expiresAt" },
        { text: "Created", value: "createdAt" },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ],
      keyNameRules: [
        value => !!(value && value.trim()) || "Key name is required",
        value => !value || value.trim().length <= 100 || "Key name must be 100 characters or less",
      ],
      form: {
        id: null,
        keyName: "",
        description: "",
        expiresAt: "",
        allowedOrigins: [],
      },
    };
  },
  created() {
    this.loadApiKeys();
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addSuccessMessage",
    ]),

    emptyForm() {
      return {
        id: null,
        keyName: "",
        description: "",
        expiresAt: "",
        allowedOrigins: [],
      };
    },

    async loadApiKeys() {
      this.loading = true;
      try {
        const response = await getAllApiKeys();
        this.apiKeys = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        this.addErrorMessage(this.getErrorMessage(error, "Unable to load API keys"));
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.form = this.emptyForm();
      this.editorDialog = true;
      this.$nextTick(() => {
        if (this.$refs.editorForm) {
          this.$refs.editorForm.resetValidation();
        }
      });
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.form = {
        id: item.id,
        keyName: item.keyName || "",
        description: item.description || "",
        expiresAt: this.toInputDateTime(item.expiresAt),
        allowedOrigins: Array.isArray(item.allowedOrigins)
          ? [...item.allowedOrigins]
          : [],
      };
      this.editorDialog = true;
      this.$nextTick(() => {
        if (this.$refs.editorForm) {
          this.$refs.editorForm.resetValidation();
        }
      });
    },

    closeEditor() {
      this.editorDialog = false;
      this.form = this.emptyForm();
    },

    buildPayload() {
      return {
        keyName: (this.form.keyName || "").trim(),
        description: (this.form.description || "").trim() || null,
        expiresAt: this.form.expiresAt || null,
        allowedOrigins: this.sanitizeOrigins(this.form.allowedOrigins),
      };
    },

    sanitizeOrigins(origins) {
      return Array.from(
        new Set(
          (origins || [])
            .map(origin => `${origin || ""}`.trim())
            .filter(origin => !!origin)
        )
      );
    },

    validateOrigins(origins) {
      for (const origin of origins) {
        try {
          const url = new URL(origin);
          if (!["http:", "https:"].includes(url.protocol)) {
            throw new Error("Invalid protocol");
          }
          if (!url.hostname) {
            throw new Error("Missing host");
          }
          if (url.username || url.password || url.search || url.hash) {
            throw new Error("Unsupported origin parts");
          }
          if (url.pathname && url.pathname !== "/") {
            throw new Error("Path is not allowed");
          }
        } catch (error) {
          this.addErrorMessage(
            `Invalid origin: ${origin}. Use a full origin like https://app.example.com`
          );
          return false;
        }
      }

      return true;
    },

    async saveApiKey() {
      if (!this.$refs.editorForm || !this.$refs.editorForm.validate()) {
        return;
      }

      const payload = this.buildPayload();
      if (!this.validateOrigins(payload.allowedOrigins)) {
        return;
      }

      this.saving = true;
      try {
        if (this.isEditing) {
          await updateApiKey({ id: this.form.id, ...payload });
          this.addSuccessMessage("API key was updated successfully");
        } else {
          const response = await createApiKey(payload);
          this.createdKey = response.data || {};
          this.createdKeyDialog = true;
          this.addSuccessMessage("API key was created successfully");
        }

        this.editorDialog = false;
        this.form = this.emptyForm();
        await this.loadApiKeys();
      } catch (error) {
        this.addErrorMessage(
          this.getErrorMessage(
            error,
            this.isEditing
              ? "Error updating API key"
              : "Error creating API key"
          )
        );
      } finally {
        this.saving = false;
      }
    },

    async deactivateKey(item) {
      if (!item.isActive) {
        return;
      }
      if (!window.confirm(`Deactivate API key "${item.keyName}"?`)) {
        return;
      }

      try {
        await deactivateApiKey(item.id);
        this.addSuccessMessage("API key was deactivated");
        await this.loadApiKeys();
      } catch (error) {
        this.addErrorMessage(this.getErrorMessage(error, "Error deactivating API key"));
      }
    },

    async deleteKey(item) {
      if (!window.confirm(`Delete API key "${item.keyName}"? This cannot be undone.`)) {
        return;
      }

      try {
        await deleteApiKey(item.id);
        this.addSuccessMessage("API key was deleted");
        await this.loadApiKeys();
      } catch (error) {
        this.addErrorMessage(this.getErrorMessage(error, "Error deleting API key"));
      }
    },

    toInputDateTime(value) {
      if (!value) {
        return "";
      }
      return value.substring(0, 16);
    },

    async copyCreatedKey() {
      if (!this.createdKey || !this.createdKey.apiKey) {
        return;
      }

      const copied = await this.copyText(this.createdKey.apiKey);
      if (copied) {
        this.addSuccessMessage("API key copied to clipboard");
      } else {
        this.addErrorMessage("Unable to copy API key automatically");
      }
    },

    async copyText(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (error) {
          return false;
        }
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();

      let copied = false;
      try {
        copied = document.execCommand("copy");
      } catch (error) {
        copied = false;
      }

      document.body.removeChild(textarea);
      return copied;
    },

    closeCreatedKeyDialog() {
      this.createdKeyDialog = false;
      this.createdKey = {};
    },

    getErrorMessage(error, fallback) {
      if (!error || !error.response) {
        return fallback;
      }
      if (typeof error.response.data === "string" && error.response.data) {
        return error.response.data;
      }
      if (error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return fallback;
    },
  },
};
</script>

<style scoped>
.api-key-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}
</style>
